import { type GetProps, isWeb, styled, View } from "tamagui";
import type { Status } from "../shared-types/status";
import { hairline } from "../utils";
import { disabledInteractiveStyles, focusVisibleRingStyle } from "./interactive/styles";

// status → the pill's colors: frame (bg + hairline border) applied by the
// `status` variant below; `text`/`icon` read by consumers for what they render
// inside (a styled frame can't color its children).
export const statusColorTokens = {
  neutral: {
    bg: "$surface0",
    border: "$borderDefault",
    text: "$textDefault",
    icon: "$iconDefault"
  },
  positive: {
    bg: "$bgPositive",
    border: "$borderPositive",
    text: "$textPositive",
    icon: "$iconPositive"
  },
  negative: {
    bg: "$bgNegative",
    border: "$borderNegative",
    text: "$textNegative",
    icon: "$iconNegative"
  },
  warning: {
    bg: "$bgWarning",
    border: "$borderWarning",
    text: "$textWarning",
    icon: "$iconWarning"
  },
  info: {
    bg: "$bgAccent",
    border: "$borderAccent",
    text: "$textAccent",
    icon: "$iconAccent"
  }
} as const satisfies Record<Status, {
  bg: string;
  border: string;
  text: string;
  icon: string;
}>;

// Truncation skin for a single-line label inside the frame; shared by Badge + Chip.
export const pillTextTruncation = {
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  style: {
    overflowX: "clip",
    overflowY: "visible",
    minWidth: 0
  }
} as const;

// The one piece of interactive feedback the skin standardises: pills are much
// smaller than cards, so the press dips further than the card default (0.98)
// to read at this size. Shared default for every interactive pill — web gets
// it via the `interactive` variant's `pressStyle`; native consumers pass it to
// `usePressScale` (or their own value, overriding `pressStyle.scale` to match).
export const PILL_PRESSED_SCALE = 0.94;
const pillWebPressScale = isWeb ? {
  scale: PILL_PRESSED_SCALE
} as const : {} as const;

// Interactive treatment, mirroring `StyledCard`'s `cardInteractiveVariantStyles`:
// the base frame carries no interactive states; the `interactive` variant layers
// them on (off by default). `Chip` toggles it on; `Badge` leaves it off. Press
// reuses hover's background (app-wide "press == hover styling" convention) plus
// the scale dip.
export const pillInteractiveStyles = {
  cursor: "pointer",
  hoverStyle: {
    backgroundColor: "$surface0Hover",
    borderColor: "$borderStrong"
  },
  pressStyle: {
    backgroundColor: "$surface0Hover",
    borderColor: "$borderStrong",
    ...pillWebPressScale
  },
  focusVisibleStyle: focusVisibleRingStyle
} as const;

/**
 * The pill skin shared by `Badge` (static) and `Chip` (interactive) — the
 * `StyledPill`/`Badge`/`Chip` trio mirrors `StyledCard`/`Card`/`SelectionCard`:
 * a base frame that is non-interactive by default, with `interactive` +
 * `disabled` variants a wrapper opts into. Skin only — full-radius frame,
 * status colours, hairline border, truncation, and the shared
 * `PILL_PRESSED_SCALE` press dip; geometry (height/padding) is
 * each consumer's own policy, since Badge and Chip share no sizes. Role-agnostic
 * — consumers set `role` + `render` (`Badge` → `span role="status"`, `Chip` →
 * its interactive tag with `interactive` on).
 */
export const StyledPill = styled(View, {
  name: "StyledPill",
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "row",
  gap: "$sp0.5",
  overflow: "hidden",
  borderRadius: "$brFull",
  // The hairline gives the pill definition against same-family surfaces.
  borderWidth: hairline,
  variants: {
    status: {
      neutral: {
        backgroundColor: statusColorTokens.neutral.bg,
        borderColor: statusColorTokens.neutral.border
      },
      positive: {
        backgroundColor: statusColorTokens.positive.bg,
        borderColor: statusColorTokens.positive.border
      },
      negative: {
        backgroundColor: statusColorTokens.negative.bg,
        borderColor: statusColorTokens.negative.border
      },
      warning: {
        backgroundColor: statusColorTokens.warning.bg,
        borderColor: statusColorTokens.warning.border
      },
      info: {
        backgroundColor: statusColorTokens.info.bg,
        borderColor: statusColorTokens.info.border
      }
    },
    // Interactive + disabled sit last so `disabled` overrides `interactive`'s
    // hover/press when both apply — same ordering contract as `StyledCard`.
    interactive: {
      true: pillInteractiveStyles,
      false: {}
    },
    disabled: {
      true: disabledInteractiveStyles,
      false: {}
    }
  } as const,
  defaultVariants: {
    status: "neutral",
    interactive: false
  }
});
export type StyledPillProps = GetProps<typeof StyledPill>;